import type { ReactNode } from 'react'
import type { ParticipantStatus } from './participant-state'
import type { SessionParticipant } from '~/gql/sessions/sessions'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { tourAnchor } from '~/helpers/tour/anchors'
import TablePagination from '~/pages/backoffice/TablePagination'
import { FilterSelect, TableToolbar, ToolbarActions } from '~/pages/backoffice/TableToolbar'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'
import { PARTICIPANT_STATUS_KEYS } from './participant-state'
import SessionParticipantsTable from './SessionParticipantsTable'

type IProps = {
  /** Participants déjà filtrés par état. */
  participants: readonly SessionParticipant[]
  state: ParticipantStatus | ''
  onStateChange: (state: ParticipantStatus | '') => void
  formatDate: (value: unknown) => string
  /** Action de chaque ligne (abandon, #324). */
  renderAction?: (participant: SessionParticipant) => ReactNode
  /** Exports de la session, posés dans la barre du tableau. */
  exports?: ReactNode
}

/** Les inscrits de la session et leurs résultats, filtrables par état (#243). */
function SessionParticipantsCard({ participants, state, onStateChange, formatDate, renderAction, exports }: IProps) {
  const { t } = useTranslation()
  const { pageRows, pagination } = useClientPagination(participants, state)

  return (
    <Card {...tourAnchor('sessionDetail.participants')}>
      <CardHeader className="flex flex-col gap-3">
        <CardTitle className="text-lg">{t('sessionDetail.participants.title')}</CardTitle>
        <TableToolbar>
          <FilterSelect
            aria-label={t('user.progress.label')}
            value={state}
            onChange={e => onStateChange(e.target.value as ParticipantStatus | '')}
            {...tourAnchor('sessionDetail.filter')}
          >
            <option value="">{t('user.progress.all')}</option>
            {Object.entries(PARTICIPANT_STATUS_KEYS).map(([value, key]) => (
              <option key={value} value={value}>{t(key)}</option>
            ))}
          </FilterSelect>
          {exports && <ToolbarActions>{exports}</ToolbarActions>}
        </TableToolbar>
      </CardHeader>
      <CardContent>
        {participants.length === 0
          ? <p className="p-4 text-center text-sm text-muted-foreground">{t('sessionDetail.participants.empty')}</p>
          : (
              <>
                <SessionParticipantsTable participants={pageRows} formatDate={formatDate} renderAction={renderAction} />
                <TablePagination {...pagination} />
              </>
            )}
      </CardContent>
    </Card>
  )
}

export default SessionParticipantsCard
